import { NO_PLACEHOLDER, YES_PLACEHOLDER } from '@occc/ui-design-system';
import {
  DateTimeFormat,
  DateTimeFormatParse,
  DateTimeFormatTZ,
  formatDateTime,
  formatDateTimeTZ,
  parseDate,
} from '@occc-app/lib/date';
import { Constraint_Purchase_Threshold_Counting_Attribute, Weekday } from '@occc/graphql';
import { WEEKDAY_SHORT } from '@occc-app/lib/util-location-schedule';
import { capitalize } from 'lodash';
import { displayAmount } from '@occc-app/lib/currency';

export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  return formatDateTime(
    parseDate([hours, minutes].join(':'), DateTimeFormatParse.Time24h),
    DateTimeFormat.Time12h,
  );
};

export const formatDaysRange = (days?: Weekday[], sep = ','): string | undefined => {
  if (!days || days.length === 0) return undefined;
  if (days.length === 7) return 'Every day';
  return days.map((day) => WEEKDAY_SHORT[day]).join(`${sep} `);
};

export const formatTimeRange = (time?: Array<[string, string]>, sep = ';'): string | undefined => {
  if (!time || time.length === 0) return undefined;
  return time
    .map(([startTime, endTime]) => {
      if (!startTime && !endTime) return undefined;
      if (startTime && endTime) return `${formatTime(startTime)} - ${formatTime(endTime)}`;
      if (startTime) return `From ${formatTime(startTime)}`;
      if (endTime) return `Until ${formatTime(endTime)}`;
      return undefined;
    })
    .filter(Boolean)
    .join(`${sep} `);
};

export const formatTimeZones = (zones?: string[], sep = ','): string | undefined => {
  if (!zones || zones.length === 0) return undefined;
  const date = new Date();
  return [
    ...new Set(
      zones
        .map((zone) =>
          zone ? formatDateTimeTZ(date, zone, DateTimeFormatTZ.ShortTimeZone) : undefined,
        )
        .filter(Boolean),
    ),
  ].join(`${sep} `);
};
export const formatYesValues = (values?: Array<unknown>, sep = ','): string | undefined => {
  if (!values || values.length === 0) return undefined;
  return (
    values
      .map((v) => (v ? YES_PLACEHOLDER : undefined))
      .filter(Boolean)
      .join(`${sep} `) || undefined
  );
};

export const formatYesNoValues = (values?: unknown[], sep = ','): string | undefined => {
  if (!values || values.length === 0) return NO_PLACEHOLDER;
  return values.map((v) => (v ? YES_PLACEHOLDER : NO_PLACEHOLDER)).join(`${sep} `);
};

export const formatValues = (values?: unknown[], sep = ','): string | undefined => {
  if (!values || values.length === 0) return undefined;
  return values.filter(Boolean).sort().join(`${sep} `);
};

export const formatEnum = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  return capitalize(value.replace(/_/g, ' '));
};

export const formatPurchaseThresholdValue = (
  countBy: Constraint_Purchase_Threshold_Counting_Attribute,
  threshold: number,
): string => {
  return countBy === Constraint_Purchase_Threshold_Counting_Attribute.MoneyAmount
    ? displayAmount(threshold)
    : String(threshold);
};
